import { MoreVertical, MapPin, DollarSign, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface JobHeaderProps {
  job: {
    job_number: string;
    owner_name: string;
    property_address: string;
    property_type: string;
    status: string;
    created_at: string;
    updated_at?: string;
    estimated_value?: number;
  };
  photoCount?: number;
  documentCount?: number;
  taskCount?: number;
  onEditJob: () => void;
  onDuplicateJob?: () => void;
  onArchiveJob?: () => void;
  onTabChange?: (tab: string) => void;
  onOpenDocuments?: () => void;
  onOpenTasks?: () => void;
  onOpenPayments?: () => void;
  onOpenTimeline?: () => void;
  onMeasureJob?: () => void;
  measuring?: boolean;
}

export const JobHeader = ({
  job,
  photoCount = 0,
  documentCount = 0,
  taskCount = 0,
  onEditJob,
  onDuplicateJob,
  onArchiveJob,
  onTabChange,
  onOpenDocuments,
  onOpenTasks,
  onOpenPayments,
  onOpenTimeline,
  onMeasureJob,
  measuring = false,
}: JobHeaderProps) => {
  const formatRelativeDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div className="relative border-b border-border/70 bg-background">
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-secondary/5 pointer-events-none" />
      <div className="container mx-auto px-6 py-3 relative">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <Badge variant="outline" className="text-[10px] font-display font-semibold border-primary/30 text-primary bg-primary/10 px-2 py-0 premium-glow">
              {job.job_number}
            </Badge>
            <h1 className="max-w-[260px] truncate text-lg font-display font-bold leading-tight text-foreground md:text-xl opacity-100">
              {job.owner_name}
            </h1>
            <span className="text-border">•</span>
            <div className="min-w-0 flex items-center gap-1.5 text-sm text-foreground">
              <MapPin className="w-3.5 h-3.5 text-primary shrink-0" />
              <span className="font-medium truncate">{job.property_address}</span>
            </div>
            <span className="text-border hidden md:inline">•</span>
            <Badge
              variant="outline"
              className={`text-[10px] font-display font-semibold px-2 py-0 ${
                job.property_type?.toLowerCase() === 'insurance'
                  ? 'border-blue-500/30 text-blue-600 bg-blue-500/10 dark:text-blue-400'
                  : job.property_type?.toLowerCase() === 'commercial'
                  ? 'border-orange-500/30 text-orange-600 bg-orange-500/10 dark:text-orange-400'
                  : 'border-green-500/30 text-green-600 bg-green-500/10 dark:text-green-400'
              }`}
            >
              {job.property_type?.toLowerCase() === 'insurance' ? 'Insurance' : job.property_type?.toLowerCase() === 'commercial' ? 'Commercial' : 'Retail'}
            </Badge>
            <Badge variant="outline" className="text-[10px] font-display border-primary/30 text-primary bg-primary/10 px-2 py-0">
              {job.status}
            </Badge>
            {job.estimated_value && job.estimated_value > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <DollarSign className="w-3.5 h-3.5 text-emerald-500" />
                <span className="text-foreground font-semibold">${job.estimated_value.toLocaleString()}</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />
              {new Date(job.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </span>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {job.updated_at && (
              <p className="hidden lg:flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                Last contacted: <span className="text-foreground font-medium">{formatRelativeDate(job.updated_at)}</span>
              </p>
            )}

            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9 premium-glow hover:bg-primary/10 hover:text-primary">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="premium-card border-primary/20">
                  <DropdownMenuItem onClick={onEditJob}>Edit Job</DropdownMenuItem>
                  <DropdownMenuItem onClick={onDuplicateJob}>
                    Duplicate Job
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onArchiveJob}>
                    Archive Job
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};